import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { environment } from '../../../../environments/environment';
import type { PackageTypeId, PackageUnitSystem } from '../package-types.config';
import {
  CUSTOMER_PACKAGING_TYPE_CODE,
  DELIVERY_INSTRUCTIONS_MAX_LENGTH,
  TRACKING_REFERENCE_MAX_LENGTH,
} from '../package-types.config';
import { convertInchesToCM, roundUpCentimetersToWholeInches } from '../package-dimension-units';
import { optionalDeliveryInstructionsCharactersValidator } from '../validators/delivery-instructions.validators';
import { optionalTrackingRestrictedCharactersValidator } from '../validators/tracking-reference.validators';

/** Default Express Pack/Box total weight when the type is selected. */
export const EXPRESS_PACK_DEFAULT_WEIGHT = 1;

/** Maximum total pieces allowed for Customer Packaging (`environment.customerMaxTotalPieces`). */
export const CUSTOMER_MAX_TOTAL_PIECES = environment.customerMaxTotalPieces;

/** Default per-piece weight for Customer Packaging rows. */
export const CUSTOMER_DEFAULT_PER_PIECE_WEIGHT = 1;

/** Pounds-to-kilograms factor for Express Pack max-weight conversion. */
const LB_TO_KG = 0.45359237;

/**
 * True when Product Master supplied a usable numeric max constraint.
 */
function hasProductMasterMax(value: number | undefined | null): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Converts a Product Master max weight in lb to the active unit system.
 * Metric values are rounded to two decimals (e.g. 70 lb → 31.75 kg).
 */
export function expressPackMaxWeightForUnit(
  unitSystem: PackageUnitSystem,
  maxWeightLb: number,
): number {
  if (unitSystem === 'imperial') {
    return maxWeightLb;
  }

  return Math.round(maxWeightLb * LB_TO_KG * 100) / 100;
}

/**
 * `EXPRESS_PACK_DEFAULT_WEIGHT` (1 lb) converted to the active unit system.
 * Used whenever a default Express weight is set/sent (e.g. on package type
 * change, or as a fallback for Express Envelope, which has no weight input),
 * so the same real-world weight is used regardless of unit system — fixes
 * OM-714, where a raw `1` sent unconverted in metric (≈1 kg ≈ 2.2 lb) could
 * exceed product max-weight constraints.
 */
export function expressPackDefaultWeightForUnit(unitSystem: PackageUnitSystem): number {
  return expressPackMaxWeightForUnit(unitSystem, EXPRESS_PACK_DEFAULT_WEIGHT);
}

/**
 * Rejects more than two decimal places on Express total weight.
 */
export function expressWeightMaxTwoDecimals(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { expressWeightInvalid: true };
    }

    const scaled = value * 100;
    if (Math.abs(scaled - Math.round(scaled)) > 1e-8) {
      return { expressWeightDecimals: true };
    }

    return null;
  }

  const asString = String(value).trim();
  if (/^(?:\d+(\.\d{1,2})?|\.\d{1,2})$/.test(asString)) {
    return null;
  }

  if (/^(?:\d+\.\d+|\.\d+)$/.test(asString)) {
    return { expressWeightDecimals: true };
  }

  return { expressWeightInvalid: true };
}

/**
 * Builds validators for Express Pack total weight for the active unit system.
 * Applies max only when Product Master provides `constraints.weight.max`.
 *
 * @param unitSystem - Active lb/kg unit system
 * @param maxWeightLb - Product Master `constraints.weight.max` in pounds, when available
 */
export function createExpressPackWeightValidators(
  unitSystem: PackageUnitSystem,
  maxWeightLb?: number,
) {
  const validators = [Validators.required, Validators.min(0.01), expressWeightMaxTwoDecimals];

  if (hasProductMasterMax(maxWeightLb)) {
    validators.splice(2, 0, Validators.max(expressPackMaxWeightForUnit(unitSystem, maxWeightLb)));
  }

  return validators;
}

/** One customer-packaging (or express) piece row in the package table. */
export interface PackagePieceRowValue {
  identicalPieces: number | null;
  perPieceWeight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  requiresAdditionalHandling: boolean;
}

/** Package step form value shape. */
export interface PackageSelectionFormValue {
  packageTypeId: PackageTypeId | null;
  unitSystem: PackageUnitSystem;
  pieces: PackagePieceRowValue[];
  trackingReferences: string[];
  /**
   * Optional delivery instructions printed in the shipment label Notes field.
   * Absent on session snapshots saved before the field existed.
   */
  deliveryInstructions?: string;
  /** Express Pack/Box total weight. */
  expressTotalWeight: number | null;
  /** Express Box additional handling; independent of customer piece-row AH. */
  expressRequiresAdditionalHandling: boolean;
}

export type PackagePieceRowGroup = FormGroup<{
  identicalPieces: FormControl<number | null>;
  perPieceWeight: FormControl<number | null>;
  length: FormControl<number | null>;
  width: FormControl<number | null>;
  height: FormControl<number | null>;
  requiresAdditionalHandling: FormControl<boolean>;
}>;

export type PackageSelectionFormGroup = FormGroup<{
  packageTypeId: FormControl<PackageTypeId | null>;
  unitSystem: FormControl<PackageUnitSystem>;
  pieces: FormArray<PackagePieceRowGroup>;
  trackingReferences: FormArray<FormControl<string>>;
  deliveryInstructions: FormControl<string>;
  expressTotalWeight: FormControl<number | null>;
  expressRequiresAdditionalHandling: FormControl<boolean>;
}>;

/**
 * Converts a Product Master max length in inches to the active unit system.
 * Metric uses ESN `convertInchesToCM` (108 in → 274.33 cm, not raw 108×2.54).
 */
export function customerDimensionMaxForUnit(
  unitSystem: PackageUnitSystem,
  maxLengthIn: number,
): number {
  if (unitSystem === 'imperial') {
    return maxLengthIn;
  }

  return convertInchesToCM(maxLengthIn);
}

/**
 * Rejects metric dimensions whose whole-inch ESN conversion exceeds the imperial max.
 * ESN rounds any fractional inch up, so 274.32 cm is 108 in and 274.33 cm is 109 in.
 * Returns Angular `{ max }` so existing Exceeds-limit messaging keeps working.
 */
export function customerMetricDimensionMax(
  maxLengthIn: number,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }

    const inches = roundUpCentimetersToWholeInches(parsed);
    if (inches > maxLengthIn) {
      return { max: { max: maxLengthIn, actual: inches } };
    }

    return null;
  };
}

/**
 * Rejects non-integer Total Identical Pieces values (whole numbers only).
 */
export function customerIdenticalPiecesInteger(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return { notInteger: true };
  }

  return null;
}

/**
 * Builds validators for Total Identical Pieces (required, integer, 1–5).
 */
export function createCustomerIdenticalPiecesValidators() {
  return [
    Validators.required,
    Validators.min(1),
    Validators.max(CUSTOMER_MAX_TOTAL_PIECES),
    customerIdenticalPiecesInteger,
  ];
}

/**
 * Builds validators for Per Piece Weight for the active unit system.
 * Same rules as Express Pack/Box total weight (required, min, optional PM max, two decimals).
 */
export function createCustomerWeightValidators(
  unitSystem: PackageUnitSystem,
  maxWeightLb?: number,
) {
  return createExpressPackWeightValidators(unitSystem, maxWeightLb);
}

/**
 * Builds validators for Length / Width / Height for the active unit system.
 * Metric rounds entered cm up to whole inches before comparing against the imperial max (ESN parity).
 * Applies max only when Product Master provides `constraints.length.max`.
 */
export function createCustomerDimensionValidators(
  unitSystem: PackageUnitSystem,
  maxLengthIn?: number,
) {
  const validators = [Validators.required, Validators.min(0.01), expressWeightMaxTwoDecimals];

  if (hasProductMasterMax(maxLengthIn)) {
    const maxValidator =
      unitSystem === 'metric'
        ? customerMetricDimensionMax(maxLengthIn)
        : Validators.max(maxLengthIn);
    validators.splice(2, 0, maxValidator);
  }

  return validators;
}

/** Default imperial validators for Customer Packaging piece fields. */
export const CUSTOMER_PIECE_IDENTICAL_VALIDATORS = createCustomerIdenticalPiecesValidators();
export const CUSTOMER_PIECE_WEIGHT_VALIDATORS = createCustomerWeightValidators('imperial');
export const CUSTOMER_PIECE_DIMENSION_VALIDATORS = createCustomerDimensionValidators('imperial');

/**
 * Coerces puro-input number-field values to a finite number (strings become numbers).
 * Returns 0 for null, empty, or non-numeric input so totals never concatenate strings.
 */
export function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Options for unit-aware Customer Packaging piece-row validators. */
export interface CustomerPieceValidatorOptions {
  unitSystem?: PackageUnitSystem;
  maxWeightLb?: number;
  maxLengthIn?: number;
}

/**
 * Sums identical piece counts across customer packaging rows.
 * Non-positive values are treated as 0 so negatives cannot lower totals or bypass max-piece gates.
 */
export function sumCustomerTotalPieces(pieces: FormArray<PackagePieceRowGroup>): number {
  return pieces.controls.reduce(
    (sum, row) => sum + Math.max(0, toFiniteNumber(row.controls.identicalPieces.value)),
    0,
  );
}

/**
 * Sums shipment weight as identical pieces × per-piece weight for each row.
 * Non-positive piece/weight values are treated as 0 so negatives cannot bypass max-weight gates.
 */
export function sumCustomerTotalWeight(pieces: FormArray<PackagePieceRowGroup>): number {
  return pieces.controls.reduce((sum, row) => {
    const identicalPieces = Math.max(0, toFiniteNumber(row.controls.identicalPieces.value));
    const perPieceWeight = Math.max(0, toFiniteNumber(row.controls.perPieceWeight.value));
    return sum + identicalPieces * perPieceWeight;
  }, 0);
}

/**
 * Enables or clears customer packaging piece-row validators on a single row.
 * When enabled, applies unit-aware max weight and dimension limits.
 */
export function applyCustomerPieceRowValidators(
  row: PackagePieceRowGroup,
  enabled: boolean,
  options: CustomerPieceValidatorOptions = {},
): void {
  if (enabled) {
    const unitSystem = options.unitSystem ?? 'imperial';
    const weightValidators = createCustomerWeightValidators(unitSystem, options.maxWeightLb);
    const dimensionValidators = createCustomerDimensionValidators(unitSystem, options.maxLengthIn);

    row.controls.identicalPieces.setValidators(createCustomerIdenticalPiecesValidators());
    row.controls.perPieceWeight.setValidators(weightValidators);
    row.controls.length.setValidators(dimensionValidators);
    row.controls.width.setValidators(dimensionValidators);
    row.controls.height.setValidators(dimensionValidators);
  } else {
    row.controls.identicalPieces.clearValidators();
    row.controls.perPieceWeight.clearValidators();
    row.controls.length.clearValidators();
    row.controls.width.clearValidators();
    row.controls.height.clearValidators();
  }

  row.controls.identicalPieces.updateValueAndValidity({ emitEvent: false });
  row.controls.perPieceWeight.updateValueAndValidity({ emitEvent: false });
  row.controls.length.updateValueAndValidity({ emitEvent: false });
  row.controls.width.updateValueAndValidity({ emitEvent: false });
  row.controls.height.updateValueAndValidity({ emitEvent: false });
}

/**
 * Creates an empty piece row for customer packaging dimensions.
 * Defaults: identical pieces 1, per-piece weight 1.
 */
export function createPackagePieceRowGroup(
  initial?: Partial<PackagePieceRowValue>,
  options: CustomerPieceValidatorOptions = {},
): PackagePieceRowGroup {
  const unitSystem = options.unitSystem ?? 'imperial';
  const weightValidators = createCustomerWeightValidators(unitSystem, options.maxWeightLb);
  const dimensionValidators = createCustomerDimensionValidators(unitSystem, options.maxLengthIn);

  return new FormGroup({
    identicalPieces: new FormControl<number | null>(initial?.identicalPieces ?? 1, {
      nonNullable: false,
      validators: createCustomerIdenticalPiecesValidators(),
    }),
    perPieceWeight: new FormControl<number | null>(
      initial?.perPieceWeight ?? CUSTOMER_DEFAULT_PER_PIECE_WEIGHT,
      {
        nonNullable: false,
        validators: weightValidators,
      },
    ),
    length: new FormControl<number | null>(initial?.length ?? null, {
      nonNullable: false,
      validators: dimensionValidators,
    }),
    width: new FormControl<number | null>(initial?.width ?? null, {
      nonNullable: false,
      validators: dimensionValidators,
    }),
    height: new FormControl<number | null>(initial?.height ?? null, {
      nonNullable: false,
      validators: dimensionValidators,
    }),
    requiresAdditionalHandling: new FormControl<boolean>(
      initial?.requiresAdditionalHandling ?? false,
      { nonNullable: true },
    ),
  });
}

/**
 * Creates a tracking reference control with max length and restricted-character validators.
 */
export function createTrackingReferenceControl(initial = ''): FormControl<string> {
  return new FormControl(initial, {
    nonNullable: true,
    validators: [
      Validators.maxLength(TRACKING_REFERENCE_MAX_LENGTH),
      optionalTrackingRestrictedCharactersValidator,
    ],
  });
}

/**
 * Creates the optional delivery instructions control.
 * Uses its own character rule: prohibited characters are rejected, while comma,
 * French letters, and other non-prohibited characters are allowed.
 */
export function createDeliveryInstructionsControl(initial = ''): FormControl<string> {
  return new FormControl(initial, {
    nonNullable: true,
    validators: [
      Validators.maxLength(DELIVERY_INSTRUCTIONS_MAX_LENGTH),
      optionalDeliveryInstructionsCharactersValidator,
    ],
  });
}

/**
 * Creates the reactive form for the Package step.
 * Customer Packaging is selected by default.
 */
export function createPackageSelectionForm(): PackageSelectionFormGroup {
  return new FormGroup({
    packageTypeId: new FormControl<PackageTypeId | null>(CUSTOMER_PACKAGING_TYPE_CODE, {
      nonNullable: false,
      validators: [Validators.required],
    }),
    unitSystem: new FormControl<PackageUnitSystem>('imperial', { nonNullable: true }),
    pieces: new FormArray<PackagePieceRowGroup>([createPackagePieceRowGroup()]),
    trackingReferences: new FormArray<FormControl<string>>([createTrackingReferenceControl()]),
    deliveryInstructions: createDeliveryInstructionsControl(),
    expressTotalWeight: new FormControl<number | null>(null, { nonNullable: false }),
    expressRequiresAdditionalHandling: new FormControl<boolean>(false, { nonNullable: true }),
  });
}

/**
 * Rounds a measurement up to the next whole number.
 */
export function roundMeasurementUp(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return value;
  }

  return Math.ceil(value);
}
